import { useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { Button } from "@/components/ui/button";

import type { BudgetLineRow } from "@/hooks/use-project-detail";
import { useSaveBudgetAssignments } from "@/hooks/use-budget-assignments";
import { isAssignableUser, type UserLookup } from "@/hooks/use-users";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";

/**
 * Draft assignment for the editor form
 */
export interface DraftAssignment {
  id?: string;         // VIB-671: bevares for UPDATE-flyt i useSaveBudgetAssignments
  _key?: string;       // stable UI key for new rows (not sent to DB)
  userId: string;
  hours: number;
  label: string;       // VIB-671 — tom streng = null i save-payload
  deadline: string;
  notes: string;
}

/**
 * Editor for assigning hours, deadline and notes to team members for a budget line.
 */
export function AssignmentEditor({
  budgetLine,
  projectId,
  users,
  onClose,
  canAssignHours = false,
}: {
  budgetLine: BudgetLineRow;
  projectId: string;
  users: UserLookup[];
  onClose: () => void;
  canAssignHours?: boolean;
}) {
  const [drafts, setDrafts] = useState<DraftAssignment[]>(
    budgetLine.assignments.length > 0
      ? budgetLine.assignments.map((a) => ({
          id: a.id,
          userId: a.user_id,
          hours: a.hours,
          label: a.label ?? "",
          deadline: a.estimated_deadline ?? "",
          notes: a.notes ?? "",
        }))
      : [{ _key: crypto.randomUUID(), userId: "", hours: 0, label: "", deadline: "", notes: "" }],
  );
  const mutation = useSaveBudgetAssignments();
  // Skjul external (regnskap/revisor) fra ansatt-velgeren.
  const assignableUsers = users.filter(isAssignableUser);

  const totalAssigned = drafts.reduce((s, d) => s + (d.hours || 0), 0);
  const budgetHours = budgetLine.quantity_hours ?? 0;
  const diff = budgetHours - totalAssigned;
  const isOverBudget = budgetHours > 0 && diff < 0;

  const addRow = () =>
    setDrafts((prev) => [
      ...prev,
      { _key: crypto.randomUUID(), userId: "", hours: 0, label: "", deadline: "", notes: "" },
    ]);
  const removeRow = (i: number) => setDrafts((prev) => prev.filter((_, idx) => idx !== i));
  const updateRow = (
    i: number,
    field: keyof DraftAssignment,
    value: string | number,
  ) =>
    setDrafts((prev) =>
      prev.map((d, idx) => (idx === i ? { ...d, [field]: value } : d)),
    );

  const handleSave = () => {
    if (isOverBudget) return;
    mutation.mutate(
      {
        budgetLineId: budgetLine.id,
        projectId,
        assignments: drafts
          .filter((d) => d.userId && d.hours > 0)
          .map((d) => ({
            id: d.id,
            user_id: d.userId,
            hours: d.hours,
            label: d.label.trim() || null,
            estimated_deadline: d.deadline || null,
            notes: d.notes || null,
          })),
      },
      { onSuccess: onClose },
    );
  };

  if (!canAssignHours) {
    return (
      <tr>
        <td colSpan={6} className="p-3 bg-muted/30">
          <div className="text-xs text-muted-foreground italic">
            Kun prosjektleder kan tildele timer.
          </div>
        </td>
      </tr>
    );
  }

  // Escape lukker uten å lagre, Enter i et timefelt lagrer (VIB-2541).
  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") {
      e.preventDefault();
      if (!mutation.isPending && !isOverBudget) handleSave();
    }
  };

  return (
    <tr>
      <td colSpan={6} className="p-3 bg-muted/30">
        <div className="space-y-2" onKeyDown={handleKeyDown}>
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Fordel timer for: {budgetLine.description}
          </p>
          {drafts.map((d, i) => (
            <div key={d.id ?? d._key ?? i} className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
              <select
                className="flex-1 min-w-[140px] h-8 rounded-md border border-input bg-background px-2 text-sm"
                value={d.userId}
                onChange={(e) => updateRow(i, "userId", e.target.value)}
              >
                <option value="">Velg ansatt…</option>
                {assignableUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  className="w-20 h-8 rounded-md border border-input bg-background px-2 text-sm text-right tabular-nums"
                  value={d.hours || ""}
                  placeholder="Timer"
                  onChange={(e) => updateRow(i, "hours", parseFloat(e.target.value) || 0)}
                />
                <span className="text-xs text-muted-foreground">t</span>
              </div>
              <input
                type="text"
                className="flex-1 min-w-[120px] h-8 rounded-md border border-input bg-background px-2 text-sm"
                value={d.label}
                placeholder={budgetLine.description ?? "Oppgavenavn"}
                maxLength={80}
                onChange={(e) => updateRow(i, "label", e.target.value)}
                title="Navn på sub-oppgave (valgfritt, maks 80 tegn)"
              />
              <input
                type="date"
                className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                value={d.deadline}
                onChange={(e) => updateRow(i, "deadline", e.target.value)}
                title="Frist"
              />
              {drafts.length > 1 && (
                <button
                  onClick={() => removeRow(i)}
                  className="text-muted-foreground hover:text-destructive"
                  aria-label="Fjern rad"
                >
                  <SrIcon name="trash2" className="w-3.5 h-3.5"  />
                </button>
              )}
            </div>
          ))}
          <div className="flex items-center justify-between pt-1 flex-wrap gap-2">
            <button
              onClick={addRow}
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            >
              <SrIcon name="plus" className="w-3 h-3"  /> Legg til person
            </button>
            <div className="flex items-center gap-3 flex-wrap">
              {budgetHours > 0 && (
                <span
                  className={cn(
                    "text-xs tabular-nums",
                    diff < 0
                      ? "text-destructive"
                      : diff > 0
                        ? "text-amber-600"
                        : "text-green-600",
                  )}
                >
                  {totalAssigned}/{budgetHours}t{" "}
                  {diff > 0
                    ? `(${diff}t ledig)`
                    : diff < 0
                      ? `(${Math.abs(diff)}t over)`
                      : "✓"}
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={onClose}
                disabled={mutation.isPending}
              >
                <SrIcon name="x" className="w-3 h-3 mr-1"  /> Avbryt
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs"
                onClick={handleSave}
                disabled={mutation.isPending || isOverBudget}
                title={isOverBudget ? "Du har tildelt mer enn budsjettet tillater" : undefined}
              >
                {mutation.isPending ? (
                  <SrIcon name="loader2" className="w-3 h-3 animate-spin mr-1"  />
                ) : (
                  <SrIcon name="check" className="w-3 h-3 mr-1"  />
                )}
                Lagre
              </Button>
            </div>
          </div>
          {mutation.isError && (
            <p className="text-xs text-destructive">{(mutation.error as Error).message}</p>
          )}
        </div>
      </td>
    </tr>
  );
}
